import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  Mail,
  Bell,
  Shield,
  Database,
  Save,
  RefreshCw,
  Globe,
  Clock,
  Info,
  Trash2,
  AlertTriangle,
  Loader2,
  Key,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

interface SystemSetting {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  type: 'text' | 'number' | 'boolean' | 'select';
  options?: { value: string; label: string }[];
  defaultValue: string | number | boolean;
}

const SYSTEM_SETTINGS: SystemSetting[] = [
  {
    key: 'ai_chat_mode',
    label: 'KI-Chat Modus',
    description: 'Mock = Vordefinierte Antworten, API = Claude API',
    icon: <Info className="w-5 h-5 text-purple-500" />,
    type: 'select',
    options: [
      { value: 'mock', label: 'Mock (Offline)' },
      { value: 'api', label: 'API (Claude)' },
    ],
    defaultValue: 'mock',
  },
  {
    key: 'ai_chat_enabled',
    label: 'KI-Chat aktiviert',
    description: 'KI-Chat Feature für alle Nutzer ein-/ausschalten',
    icon: <Info className="w-5 h-5 text-teal-500" />,
    type: 'boolean',
    defaultValue: true,
  },
  {
    key: 'oauth_google_enabled',
    label: 'Google Sign-In aktiviert',
    description: 'Ermöglicht Anmeldung mit Google-Account. Erfordert Konfiguration in Supabase Dashboard (Authentication > Providers > Google).',
    icon: <Key className="w-5 h-5 text-blue-500" />,
    type: 'boolean',
    defaultValue: false,
  },
  {
    key: 'oauth_apple_enabled',
    label: 'Apple Sign-In aktiviert',
    description: 'Ermöglicht Anmeldung mit Apple-Account (iOS & Web). Erfordert Konfiguration in Supabase Dashboard (Authentication > Providers > Apple).',
    icon: <Key className="w-5 h-5 text-slate-800" />,
    type: 'boolean',
    defaultValue: false,
  },
  {
    key: 'deeplinks_enabled',
    label: 'DeepLinks / App-Weiterleitung aktiviert',
    description: 'Aktiviert Smart App Banner und Weiterleitung zur nativen App. Deaktivieren, solange die App nicht im Store ist.',
    icon: <Globe className="w-5 h-5 text-indigo-500" />,
    type: 'boolean',
    defaultValue: false,
  },
  {
    key: 'app_maintenance_mode',
    label: 'Wartungsmodus',
    description: 'App für alle Nutzer in den Wartungsmodus versetzen',
    icon: <Settings className="w-5 h-5 text-amber-500" />,
    type: 'boolean',
    defaultValue: false,
  },
  {
    key: 'app_version_min',
    label: 'Minimale App-Version',
    description: 'Ältere App-Versionen erhalten ein Update-Hinweis',
    icon: <Globe className="w-5 h-5 text-blue-500" />,
    type: 'text',
    defaultValue: '1.0.0',
  },
  {
    key: 'email_notifications_enabled',
    label: 'E-Mail-Benachrichtigungen',
    description: 'System-E-Mails an Nutzer aktivieren',
    icon: <Mail className="w-5 h-5 text-teal-500" />,
    type: 'boolean',
    defaultValue: true,
  },
  {
    key: 'push_notifications_enabled',
    label: 'Push-Benachrichtigungen',
    description: 'Push-Benachrichtigungen für die App aktivieren',
    icon: <Bell className="w-5 h-5 text-purple-500" />,
    type: 'boolean',
    defaultValue: true,
  },
  {
    key: 'data_retention_days',
    label: 'Datenspeicherung (Tage)',
    description: 'Wie lange inaktive Nutzerdaten aufbewahrt werden',
    icon: <Database className="w-5 h-5 text-slate-500" />,
    type: 'number',
    defaultValue: 365,
  },
  {
    key: 'session_timeout_minutes',
    label: 'Session-Timeout (Minuten)',
    description: 'Automatischer Logout nach Inaktivität',
    icon: <Clock className="w-5 h-5 text-rose-500" />,
    type: 'number',
    defaultValue: 60,
  },
  {
    key: 'default_language',
    label: 'Standard-Sprache',
    description: 'Standard-Sprache für neue Nutzer',
    icon: <Globe className="w-5 h-5 text-emerald-500" />,
    type: 'select',
    options: [
      { value: 'de', label: 'Deutsch' },
      { value: 'en', label: 'English' },
    ],
    defaultValue: 'de',
  },
  {
    key: 'gdpr_deadline_days',
    label: 'DSGVO-Frist (Tage)',
    description: 'Frist für die Bearbeitung von DSGVO-Anfragen',
    icon: <Shield className="w-5 h-5 text-blue-500" />,
    type: 'number',
    defaultValue: 30,
  },
];

export function SystemSettingsPage() {
  const queryClient = useQueryClient();
  const [editedValues, setEditedValues] = useState<Record<string, string | number | boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletedCounts, setDeletedCounts] = useState<Record<string, number> | null>(null);

  // Fetch current settings
  const { data: settings, isLoading, refetch } = useQuery({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const keys = SYSTEM_SETTINGS.map(s => s.key);
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', keys);

      if (error) throw error;

      const settingsMap: Record<string, unknown> = {};
      (data as { key: string; value: unknown }[])?.forEach(s => {
        settingsMap[s.key] = s.value;
      });

      // Initialize edited values
      const initialValues: Record<string, string | number | boolean> = {};
      SYSTEM_SETTINGS.forEach(setting => {
        const value = settingsMap[setting.key];
        if (value !== undefined) {
          initialValues[setting.key] = value as string | number | boolean;
        } else {
          initialValues[setting.key] = setting.defaultValue;
        }
      });
      setEditedValues(initialValues);

      return settingsMap;
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, string | number | boolean>) => {
      for (const [key, value] of Object.entries(values)) {
        const { data: existing } = await supabase
          .from('system_settings')
          .select('key')
          .eq('key', key)
          .single();

        if (existing) {
          const { error } = await supabase
            .from('system_settings')
            .update({ value, updated_at: new Date().toISOString() } as never)
            .eq('key', key);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('system_settings')
            .insert({ key, value } as never);
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      setHasChanges(false);
      toast.success('Einstellungen gespeichert');
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });

  // Delete all user data mutation
  const deleteUserDataMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('admin_delete_all_user_data');
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setDeletedCounts(data as Record<string, number>);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      toast.success('Alle Nutzerdaten wurden gelöscht');
      queryClient.invalidateQueries();
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const handleDeleteAllUserData = () => {
    if (deleteConfirmText === 'ALLE DATEN LÖSCHEN') {
      deleteUserDataMutation.mutate();
    }
  };

  const handleValueChange = (key: string, value: string | number | boolean) => {
    setEditedValues(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(editedValues);
  };

  const getValue = (key: string, defaultValue: string | number | boolean) => {
    return editedValues[key] ?? settings?.[key] ?? defaultValue;
  };

  const renderInput = (setting: SystemSetting) => {
    const value = getValue(setting.key, setting.defaultValue);

    switch (setting.type) {
      case 'boolean':
        return (
          <button
            onClick={() => handleValueChange(setting.key, !value)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              value ? 'bg-teal-500' : 'bg-slate-300'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                value ? 'translate-x-6' : ''
              }`}
            />
          </button>
        );
      case 'number':
        return (
          <input
            type="number"
            value={value as number}
            onChange={(e) => handleValueChange(setting.key, parseInt(e.target.value) || 0)}
            className="w-32 px-3 py-1.5 border border-slate-300 rounded-lg text-right focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
          />
        );
      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
          >
            {setting.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            className="w-48 px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
          />
        );
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">System</h1>
            <p className="text-slate-600">System-Einstellungen konfigurieren</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => refetch()}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
              title="Aktualisieren"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              Speichern
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500">Lädt Einstellungen...</div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-200">
          {SYSTEM_SETTINGS.map((setting) => (
            <div key={setting.key} className="p-6 flex items-center justify-between">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  {setting.icon}
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">{setting.label}</h3>
                  <p className="text-sm text-slate-500 mt-1">{setting.description}</p>
                </div>
              </div>
              {renderInput(setting)}
            </div>
          ))}
        </div>
      )}

      {/* Danger Zone */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Gefahrenzone</h2>
        <div className="space-y-4">
          {/* Wartungsmodus */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-red-800">Wartungsmodus aktivieren</h3>
                <p className="text-sm text-red-700 mt-1">
                  Im Wartungsmodus können sich keine Nutzer anmelden. Nur Admins haben Zugriff.
                </p>
                <button
                  onClick={() => {
                    const currentValue = getValue('app_maintenance_mode', false);
                    handleValueChange('app_maintenance_mode', !currentValue);
                  }}
                  className={`mt-3 px-4 py-2 rounded-lg font-medium ${
                    getValue('app_maintenance_mode', false)
                      ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                >
                  {getValue('app_maintenance_mode', false)
                    ? 'Wartungsmodus deaktivieren'
                    : 'Wartungsmodus aktivieren'}
                </button>
              </div>
            </div>
          </div>

          {/* Alle Nutzerdaten löschen */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-red-800">Alle Nutzerdaten löschen</h3>
                <p className="text-sm text-red-700 mt-1">
                  Löscht alle Nutzerdaten (Profile, Schwangerschaften, Partner-Verbindungen, KI-Chats, etc.).
                  Admin-Accounts werden beibehalten. Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Alle Nutzerdaten löschen
                </button>
              </div>
            </div>
          </div>

          {/* Deleted counts summary */}
          {deletedCounts && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-emerald-800">Löschung abgeschlossen</h3>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-emerald-700">
                    {Object.entries(deletedCounts).map(([key, count]) => (
                      <div key={key}>
                        <span className="font-medium">{count}</span> {key.replace(/_/g, ' ')}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Alle Nutzerdaten löschen?</h3>
                <p className="text-sm text-slate-500">Diese Aktion kann nicht rückgängig gemacht werden</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                <strong>Folgende Daten werden gelöscht:</strong>
              </p>
              <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                <li>Alle Benutzerkonten (außer Admins)</li>
                <li>Alle Profile und Schwangerschaften</li>
                <li>Alle Partner-Verbindungen</li>
                <li>Alle KI-Chat-Verläufe</li>
                <li>Alle Benachrichtigungen</li>
                <li>Alle Termine, Feedback, Anfragen</li>
              </ul>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Gib zur Bestätigung <span className="font-mono bg-slate-100 px-1 rounded">ALLE DATEN LÖSCHEN</span> ein:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                placeholder="ALLE DATEN LÖSCHEN"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteAllUserData}
                disabled={deleteConfirmText !== 'ALLE DATEN LÖSCHEN' || deleteUserDataMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleteUserDataMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Lösche...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Endgültig löschen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800">Hinweis</h3>
            <p className="text-sm text-blue-700 mt-1">
              Einige Einstellungen erfordern möglicherweise einen Neustart der Dienste,
              um vollständig wirksam zu werden. Kritische Änderungen werden protokolliert.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
